import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@patient-watch/ui/dialog";
import { CheckCircle2, XCircle } from "lucide-react";
import dayjs from "dayjs";
import type { DayStats } from "../utils/studyTrackerHelpers";
import { formatStudyNumber } from "../utils/studyTrackerHelpers";
import { cn } from "@patient-watch/ui/utils";

interface StudyTrackerDayDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    dayStats: DayStats | null;
    date: dayjs.Dayjs | null;
}

export function StudyTrackerDayDialog({ open, onOpenChange, dayStats, date }: StudyTrackerDayDialogProps) {
    if (!dayStats || !date) {
        return null;
    }

    const completionRate = dayStats.total > 0 ? dayStats.completed / dayStats.total : 0;
    const completionPercentage = Math.round(completionRate * 100);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{date.format("D MMMM YYYY")}</DialogTitle>
                    <DialogDescription>Study completion status for this day</DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    {/* Completion Stats */}
                    <div className="flex flex-col gap-2 bg-slate-50 p-4 rounded-lg">
                        <div className="flex justify-between items-center">
                            <span className="font-semibold text-slate-700">Completion Rate</span>
                            <span
                                className={cn(
                                    "font-bold text-lg",
                                    completionRate === 1
                                        ? "text-green-600"
                                        : completionRate > 0
                                          ? "text-amber-600"
                                          : "text-red-600"
                                )}
                            >
                                {completionPercentage}%
                            </span>
                        </div>
                        <div className="text-slate-600 text-sm">
                            {dayStats.completed} of {dayStats.total} profiles completed
                        </div>
                    </div>

                    {/* Completed Study Numbers */}
                    {dayStats.completedStudyNumbers.length > 0 && (
                        <div className="space-y-2">
                            <h3 className="font-semibold text-slate-900 text-sm">
                                Completed ({dayStats.completedStudyNumbers.length})
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {dayStats.completedStudyNumbers.map((studyNumber) => (
                                    <div
                                        key={studyNumber}
                                        className="flex items-center gap-1.5 bg-green-50 px-3 py-1.5 border border-green-200 rounded-md"
                                    >
                                        <CheckCircle2 size={16} className="text-green-600" />
                                        <span className="font-medium text-green-800 text-sm">
                                            {formatStudyNumber(studyNumber)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Incomplete Study Numbers */}
                    {dayStats.incompleteStudyNumbers.length > 0 && (
                        <div className="space-y-2">
                            <h3 className="font-semibold text-slate-900 text-sm">
                                Not Completed ({dayStats.incompleteStudyNumbers.length})
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {dayStats.incompleteStudyNumbers.map((studyNumber) => (
                                    <div
                                        key={studyNumber}
                                        className="flex items-center gap-1.5 bg-red-50 px-3 py-1.5 border border-red-200 rounded-md"
                                    >
                                        <XCircle size={16} className="text-red-600" />
                                        <span className="font-medium text-red-800 text-sm">
                                            {formatStudyNumber(studyNumber)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
